import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	DashboardFirstSteps,
	DashboardIssue,
	DashboardIssueType,
	DashboardListWithNewProspects,
	DashboardRepliesSection,
	DashboardResponse,
} from "@/pages/Private/pages/Dashboard/schema/dashboard";

import { dashboardApi } from "./dashboard.api";

interface DashboardState {
	data: DashboardResponse | null;
	hasLoaded: boolean;
}

const emptySection: DashboardRepliesSection = { items: [], total: 0 };

const emptyFirstSteps: DashboardFirstSteps = {
	hasCreatedOneList: false,
	hasConnectedLinkedinProfile: false,
	hasConnectedOneEMail: false,
	hasCreatedOneProfileTrackerList: false,
	hasReviewedAndActivatedTheLeadScoring: false,
	hasReviewedTheDeepResearchPrompt: false,
	hasTestedTheDeepResearchPromptOnOneProspect: false,
	hasCreatedFirstCampaign: false,
	hasReachedOutToFirstLeads: false,
	hasConnectedSignalistToOneCrm: false,
};

export const emptyDashboard: DashboardResponse = {
	myReplies: emptySection,
	companyReplies: emptySection,
	reEngage: emptySection,
	newDrafts: emptySection,
	listsWithNewProspects: [],
	issues: [],
	firstSteps: emptyFirstSteps,
};

const initialState: DashboardState = {
	data: null,
	hasLoaded: false,
};

const matchesIssue = (issue: DashboardIssue, type: DashboardIssueType, entityId?: number | null) =>
	issue.type === type && (entityId ?? null) === issue.entityId;

const toNumber = (value: unknown): number => {
	const n = Number(value);

	return Number.isFinite(n) ? n : 0;
};

const normalizeSection = (section: DashboardRepliesSection): DashboardRepliesSection => ({
	items: section?.items ?? [],
	total: toNumber(section?.total),
});

const normalizeListWithNewProspects = (
	list: DashboardListWithNewProspects
): DashboardListWithNewProspects => ({
	...list,
	newProspectCount: toNumber(list?.newProspectCount),
	prospects: list?.prospects ?? [],
});

// The shared API base does not run responseSchema parsing, so numeric fields can
// arrive as strings. Coerce totals/counts here so arithmetic (e.g. summing replies)
// does not turn into string concatenation ("1" + "0" === "10").
const normalizeDashboard = (payload: DashboardResponse): DashboardResponse => ({
	...payload,
	myReplies: normalizeSection(payload.myReplies),
	companyReplies: normalizeSection(payload.companyReplies),
	reEngage: payload.reEngage ? normalizeSection(payload.reEngage) : emptySection,
	newDrafts: normalizeSection(payload.newDrafts),
	listsWithNewProspects: (payload.listsWithNewProspects ?? []).map(normalizeListWithNewProspects),
});

export const dashboardSlice = createSlice({
	name: "dashboard",
	initialState,
	reducers: {
		removeIssueLocally: (
			state,
			action: PayloadAction<{ type: DashboardIssueType; entityId?: number | null }>
		) => {
			if (!state.data) {
				return;
			}

			state.data.issues = state.data.issues.filter(
				(issue) => !matchesIssue(issue, action.payload.type, action.payload.entityId)
			);
		},
		clearDashboard: (state) => {
			state.data = null;
			state.hasLoaded = false;
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(dashboardApi.endpoints.getDashboard.matchFulfilled, (state, { payload }) => {
			state.data = payload ? normalizeDashboard(payload) : emptyDashboard;
			state.hasLoaded = true;
		});
	},
});

export const { removeIssueLocally, clearDashboard } = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;
export const dashboardSelector = (state: RootState) => state[dashboardSlice.name];
